import axios, { type AxiosRequestConfig } from 'axios';
import { toAppError } from './appError';
import { isIResultData, type IResultData } from './result';
import { sendWithRetry, type IRetryOptions } from './retry';

export { isIResultData, type IResultData } from './result';
export { parseData } from './parseData';
export type { IRetryOptions } from './retry';

const instance = axios.create({
  baseURL: import.meta.env.PUBLIC_API_BASE_PATH,
  timeout: 30 * 1000, // 30秒超时
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  },
  withCredentials: true
});

// 统一在此归一化,下游只需处理 AppError 一种错误形态
instance.interceptors.response.use(
  (res) => {
    if (isIResultData(res.data) && res.data.code !== 200) {
      return Promise.reject(toAppError(res.data));
    }
    return res;
  },
  (error) => {
    return Promise.reject(toAppError(error));
  }
);

/**
 * retry 与 timeout 必须同时出现:总耗时由两者相乘决定,
 * 漏写 timeout 会静默继承实例的 30s,count: 2 就成了最坏 91.5s。
 */
export type TRequestConfig = AxiosRequestConfig & ({ retry?: undefined } | { retry: IRetryOptions; timeout: number });

// 重试包在 axios 外侧:拦截器里 code !== 200 那条 reject 的是裸 IResultData,不带 config,重发不了
const send = <T>({ retry, ...config }: AxiosRequestConfig & { retry?: IRetryOptions }) => {
  const call = () => instance.request<IResultData<T>>(config);

  return retry ? sendWithRetry(call, retry) : call();
};

const get = <T = unknown>(url: string, config?: TRequestConfig) => send<T>({ ...config, method: 'get', url });

const post = <T = unknown>(url: string, data?: unknown, config?: TRequestConfig) =>
  send<T>({ ...config, method: 'post', url, data });

const put = <T = unknown>(url: string, data?: unknown, config?: TRequestConfig) =>
  send<T>({ ...config, method: 'put', url, data });

const patch = <T = unknown>(url: string, data?: unknown, config?: TRequestConfig) =>
  send<T>({ ...config, method: 'patch', url, data });

const del = <T = unknown>(url: string, config?: TRequestConfig) => send<T>({ ...config, method: 'delete', url });

// 不再导出 isAxiosError：拦截器已把错误统一成 AppError，对它做 axios 判断永远为 false。
// 需要判别错误类型请用 @/utils/error 的 isAppError / isUnauthorized，或 toAppError 取 kind。
export const request = {
  get,
  post,
  put,
  patch,
  delete: del
};
